import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { faEnvelope, faEye, faLock, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClavaContext } from "../../../../config/contexts";
import { translate } from "../../../../config/translator";
import { emailOrTel, errorLogging } from "../../../../config/utils";
import { IdentifierType } from "../../../../config/types";
import ClavaTextInput from "../../../components/ClavaTextInput";
import RoundedRectangle from "../../../components/RoundedRectangle";
import SegmentedTabs from "../../../components/SegmentedTabs";
import { useReCaptcha } from "../../../../hooks/useReCaptcha";
import { RECAPTCHA_ACTION_PW_FORGOT } from "../../../../config/constants";
import client, { callClientFunc } from "../../../../client";

type Step = "identifier" | "code" | "password" | "success";

const PwForgot: React.FC = () => {
  const { l } = useContext(ClavaContext);
  const { redirectAfter } = useParams();
  
  const [step, setStep] = useState<Step>("identifier");
  const [identifier, setIdentifier] = useState<string>("");
  const [identifierType, setIdentifierType] = useState<IdentifierType>("both");
  const [code, setCode] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordRepeat, setPasswordRepeat] = useState<string>("");
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [passwordRepeatVisible, setPasswordRepeatVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  
  const retryRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  
  const { reCaptchaToken, refresh } = useReCaptcha(RECAPTCHA_ACTION_PW_FORGOT);
  const resendPendingRef = useRef<boolean>(false);
  
  const canResend = useMemo(() => secondsLeft <= 0 && reCaptchaToken !== undefined, [secondsLeft, reCaptchaToken]);

  type KnownApiError = {
    status?: number;
    body?: unknown;
    message?: string;
  };

  const extractApiErrorMessage = useCallback((err: unknown, fallback: string): string => {
    const e = err as KnownApiError | undefined;
    const body = e?.body as unknown;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && typeof body === "object") {
      const maybeDetail = (body as { detail?: Array<{ msg?: string; message?: string }> }).detail;
      if (Array.isArray(maybeDetail) && maybeDetail.length > 0) {
        return maybeDetail[0].msg || maybeDetail[0].message || fallback;
      }
      const maybeMessage = (body as { message?: string }).message;
      if (maybeMessage) return maybeMessage;
    }
    if (e?.message) return e.message;
    return fallback;
  }, []);

  const logError = useCallback((e: unknown) => {
    errorLogging(typeof e === "string" ? e : (e ?? { message: "error" }));
  }, []);

  useEffect(() => {
    if (step === "code" && secondsLeft > 0) {
      const timer = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
      return () => window.clearInterval(timer);
    }
    return undefined;
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step === "code" && secondsLeft === 0) {
      refresh();
    }
  }, [step, secondsLeft, refresh]);

  useEffect(() => {
    if (retryRef.current && reCaptchaToken && identifier && !loading && retryCountRef.current < 1) {
      retryRef.current = false;
      retryCountRef.current += 1;
      setLoading(true);
      callClientFunc(
        client().pwForgotTel,
        false,
        identifier.toLowerCase(),
        reCaptchaToken
      ).then(
        () => {
          retryCountRef.current = 0;
          setStep("code");
          setSecondsLeft(60);
          setLoading(false);
        },
        (retryErr) => {
          logError(retryErr);
          retryCountRef.current = 0;
          const e = retryErr as { status?: number } | undefined;
          if (e?.status === 429) {
            setError("Too many requests. Please wait a moment and try again.");
          } else if (e?.status === 498) {
            setError("ReCAPTCHA error. Please refresh the page and try again.");
          } else {
            setError(extractApiErrorMessage(retryErr, "Failed to send code. Please try again."));
          }
          setLoading(false);
        }
      );
    }
  }, [reCaptchaToken, identifier, loading, extractApiErrorMessage, logError]);

  const onChangeIdentifier = useCallback((e: string) => {
    setIdentifierType(emailOrTel(e));
    setIdentifier(e);
    setError(null);
  }, []);

  const sendCodeInternal = useCallback(async (token: string) => {
    if (!identifier || identifier.length === 0) {
      setError("Please enter your email or phone number");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await callClientFunc(client().pwForgotTel, false, identifier.toLowerCase(), token);
      setStep("code");
      setSecondsLeft(60);
    } catch (err) {
      logError(err);
      const e = err as { status?: number } | undefined;
      if (e?.status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
      } else if (e?.status === 498 && retryCountRef.current === 0) {
        retryRef.current = true;
        refresh();
        return;
      } else if (e?.status === 498) {
        setError("ReCAPTCHA error. Please refresh the page and try again.");
      } else {
        setError(extractApiErrorMessage(err, "Failed to send code. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }, [identifier, refresh, extractApiErrorMessage, logError]);

  const sendCode = useCallback(async () => {
    if (!identifier || identifier.length === 0) {
      setError("Please enter your email or phone number");
      return;
    }

    if (!reCaptchaToken) {
      refresh();
      setError("ReCAPTCHA not ready. Please wait a moment...");
      return;
    }
    
    await sendCodeInternal(reCaptchaToken);
  }, [identifier, reCaptchaToken, refresh, sendCodeInternal]);

  const resendCode = useCallback(() => {
    if (!identifier || identifier.length === 0) {
      setError("Please enter your email or phone number");
      return;
    }
    if (secondsLeft > 0) return;
    resendPendingRef.current = true;
    setError(null);
    refresh();
  }, [identifier, secondsLeft, refresh]);

  useEffect(() => {
    if (resendPendingRef.current && reCaptchaToken && step === "code" && secondsLeft <= 0) {
      resendPendingRef.current = false;
      callClientFunc(
        client().pwForgotTel,
        false,
        identifier.toLowerCase(),
        reCaptchaToken
      ).then(
        () => {
          setCode("");
          setSecondsLeft(60);
          setError(null);
        },
        (err) => {
          logError(err);
          setError(extractApiErrorMessage(err, "Failed to resend code. Please try again."));
          if (err?.status === 498) {
            refresh();
          }
        }
      );
    }
  }, [reCaptchaToken, step, secondsLeft, identifier, refresh, extractApiErrorMessage, logError]);

  const checkCode = useCallback(async () => {
    if (!code || code.length === 0) {
      setError("Please enter the code");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await callClientFunc(client().checkPwResetCode, false, identifier.toLowerCase(), code);
      if (res) {
        setStep("password");
        setError(null);
      } else {
        setError("Invalid code. Please try again.");
      }
    } catch (err) {
      logError(err);
      setError("Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [code, identifier, logError]);

  const submitNewPassword = useCallback(async () => {
    if (password !== passwordRepeat) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await callClientFunc(client().pwResetNew, false, identifier.toLowerCase(), password, code);
      setStep("success");
    } catch (err) {
      logError(err);
      setError(extractApiErrorMessage(err, "Failed to reset password. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [identifier, password, passwordRepeat, code, extractApiErrorMessage, logError]);

  const onBack = useCallback(() => {
    if (step === "code") {
      setStep("identifier");
      setCode("");
    } else if (step === "password") {
      setStep("code");
      setPassword("");
      setPasswordRepeat("");
    }
    setError(null);
  }, [step]);

  const onKeyUpCode = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && code.length > 0) {
      await checkCode();
    }
  }, [code, checkCode]);

  const onKeyUpPassword = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && password.length >= 8 && password === passwordRepeat) {
      await submitNewPassword();
    }
  }, [password, passwordRepeat, submitNewPassword]);

  const onKeyUpIdentifier = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && identifier.length > 0) {
      await sendCode();
    }
  }, [identifier, sendCode]);

  return (
    <div className="w-full px-6 xl:px-4 mx-auto xl:max-w-screen-xl">
      <div className="w-full flex flex-col items-start justify-start pt-2 pb-6 -mt-6 xl:-mt-8">
        <div className="max-w-xl w-full">
          <RoundedRectangle
            hasTitle
            titleType="upLeft"
            titleKey="pwForgot"
            hasBorder
            className="bg-bg-secondary dark:bg-bg-secondary-dark border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark"
          >
            <div className="p-6 xl:p-4">
              {error && (
                <span className="text-red font-bold block mb-4">
                  {error}
                </span>
              )}

              {step === "identifier" && (
                <>
                  <div className="my-4 w-full">
                    <ClavaTextInput
                      onChange={onChangeIdentifier}
                      value={identifier}
                      type="text"
                      error={false}
                      placeholder="max@mustermann.com"
                      label={translate(
                        identifierType === "both"
                          ? "mailOrTel"
                          : identifierType === "tel"
                          ? "telIdentifier"
                          : "mailAddress",
                        l
                      )}
                      iconL={identifierType === "tel" ? faPhone : faEnvelope}
                      autoComplete="email"
                      onKeyUp={onKeyUpIdentifier}
                    />
                  </div>
                  <div className="my-4 w-full flex flex-row items-center justify-between gap-2">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      items={[{
                        key: "back",
                        label: translate("back", l),
                        to: "/login",
                      }]}
                      activeKey="back"
                    />
                    <SegmentedTabs
                      className="p-0 gap-2"
                      variant="primary"
                      items={[{
                        key: "continue",
                        label: loading ? translate("loading", l) : translate("continue", l),
                        onClick: sendCode,
                        disabled: !identifier || loading,
                      }]}
                      activeKey={!loading && identifier ? "continue" : undefined}
                    />
                  </div>
                </>
              )}

              {step === "code" && (
                <>
                  <p className="text-font-600 mb-4">
                    {translate("pwForgotCodeSent", l).replace(/{identifier}/g, identifier)}
                  </p>
                  <div className="my-4 w-full">
                    <ClavaTextInput
                      onChange={setCode}
                      value={code}
                      type="text"
                      placeholder="00000"
                      label={translate("code", l)}
                      onKeyUp={onKeyUpCode}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div className="my-4 w-full flex items-center justify-between">
                    <div>
                      <SegmentedTabs
                        className="p-0 gap-2"
                        items={[{
                          key: "back",
                          label: translate("back", l),
                          onClick: onBack,
                        }]}
                        activeKey="back"
                      />
                    </div>
                    <div className="flex justify-end">
                      <SegmentedTabs
                        className="p-0 gap-2"
                        variant="primary"
                        items={[{
                          key: "continue",
                          label: loading ? translate("loading", l) : translate("continue", l),
                          onClick: checkCode,
                          disabled: !code || loading,
                        }]}
                        activeKey={!loading && code ? "continue" : undefined}
                      />
                    </div>
                  </div>
                  <div className="w-full flex justify-end">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      items={[{
                        key: "resend",
                        label: canResend 
                          ? translate("resendCode", l) 
                          : `${translate("resendAvailableIn", l)} ${Math.max(0, secondsLeft)}s`,
                        onClick: resendCode,
                        disabled: !canResend || !!code,
                      }]}
                      activeKey={canResend && !code ? "resend" : undefined}
                    />
                  </div>
                </>
              )}

              {step === "password" && (
                <>
                  <p className="text-font-600 mb-4">
                    {translate("pwForgotNewPassword", l)}
                  </p>
                  <div className="my-4 w-full">
                    <div className="relative">
                      <ClavaTextInput
                        onChange={setPassword}
                        value={password}
                        type={passwordVisible ? "text" : "password"}
                        placeholder="password"
                        label={translate("newPassword", l)}
                        iconL={faLock}
                        onKeyUp={onKeyUpPassword}
                        autoComplete="new-password"
                        error={password.length > 0 && password.length < 8}
                      />
                      <button
                        className={`w-4 h-4 bottom-3.5 right-3 absolute ${
                          passwordVisible ? " !text-primary" : ""
                        }`}
                        onClick={() => setPasswordVisible((v) => !v)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                    {password.length > 0 && password.length < 8 && (
                      <span className="text-xs text-red mt-1 block">
                        {translate("pwMinLength", l)}
                      </span>
                    )}
                  </div>
                  <div className="my-4 w-full">
                    <div className="relative">
                      <ClavaTextInput
                        onChange={setPasswordRepeat}
                        value={passwordRepeat}
                        type={passwordRepeatVisible ? "text" : "password"}
                        placeholder="password"
                        label={translate("repeatPassword", l)}
                        iconL={faLock}
                        onKeyUp={onKeyUpPassword}
                        autoComplete="new-password"
                        error={passwordRepeat.length > 0 && password !== passwordRepeat}
                      />
                      <button
                        className={`w-4 h-4 bottom-3.5 right-3 absolute ${
                          passwordRepeatVisible ? " !text-primary" : ""
                        }`}
                        onClick={() => setPasswordRepeatVisible((v) => !v)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                    {passwordRepeat.length > 0 && password !== passwordRepeat && (
                      <span className="text-xs text-red mt-1 block">
                        {translate("pwNotSame", l)}
                      </span>
                    )}
                  </div>
                  <div className="my-4 w-full flex items-center justify-between">
                    <div>
                      <SegmentedTabs
                        className="p-0 gap-2"
                        items={[{
                          key: "back",
                          label: translate("back", l),
                          onClick: onBack,
                        }]}
                        activeKey="back"
                      />
                    </div>
                    <div className="flex justify-end">
                      <SegmentedTabs
                        className="p-0 gap-2"
                        variant="primary"
                        items={[{
                          key: "reset",
                          label: loading ? translate("resetting", l) : translate("resetPassword", l),
                          onClick: submitNewPassword,
                          disabled: password.length < 8 || password !== passwordRepeat || loading,
                        }]}
                        activeKey={!loading && password.length >= 8 && password === passwordRepeat ? "reset" : undefined}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === "success" && (
                <>
                  <p className="text-font-600 mb-4">
                    {translate("resetPw", l)}
                  </p>
                  <div className="my-2">
                    <span className="text-green font-bold">
                      {translate("logInSuccess", l)}
                    </span>
                  </div>
                  <div className="my-4 w-full flex justify-end">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      variant="primary"
                      items={[{
                        key: "continue",
                        label: translate("continue", l),
                        onClick: () => {
                        const target = redirectAfter ? `/${redirectAfter}` : "/home";
                        try {
                          if (window.location.pathname === target) {
                            window.location.reload();
                          } else {
                            window.location.replace(target);
                          }
                        } catch (_) {
                          window.location.href = target;
                        }
                      },
                      }]}
                    />
                  </div>
                </>
              )}
            </div>
          </RoundedRectangle>
        </div>
      </div>
    </div>
  );
};

export default PwForgot;

